import React from 'react';
import moment from 'moment';
import codes from 'country-calling-code';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { find } from 'lodash';
import { TextField, Select } from 'formik-material-ui';

import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import DialogPaper from '../DialogPaper';

import * as ProfileService from '../../api/profileService';

export interface Values {
  countryCode: string;
  mobile: string;
}

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.common.white,
    color: '#000000',
    marginBottom: theme.spacing(6),
  },
  buttonContainer: {
    width: 100,
    margin: theme.spacing(),
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  toolbar: {
    display: 'flex',
    paddingLeft: 12,
    paddingRight: 12,
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  actions: {
    marginLeft: 'auto',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    [theme.breakpoints.down('xs')]: {
      width: 150,
    },
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  profile: ProfileService.Profile;
  confirmUpdate: (status: boolean) => void;
};

export default function EditMobileDialog({
  profile,
  onClose,
  open,
  confirmUpdate,
}: Props) {
  const classes = useStyles();
  const ValidationSchema = Yup.object().shape({
    countryCode: Yup.string().required('Required'),
    mobile: Yup.string().required('Required').nullable(),
  });

  const initialValues: Values = {
    countryCode: 'US',
    mobile: '',
  };

  const handleSubmit = async (
    values: Values,
    { setFieldError, setSubmitting }: FormikHelpers<Values>
  ) => {
    if (values.mobile && values.countryCode) {
      const callingCode = find(codes, function (code) {
        return code.isoCode2 === values.countryCode;
      });
      if (callingCode) {
        const phoneNum =
          '+' + callingCode.countryCodes + ' ' + values.mobile.trim();
        let dobStr: string | null = null;
        if (profile.dob) {
          dobStr = moment.unix(profile.dob).format('YYYY-MM-DD');
        }
        try {
          await ProfileService.update(
            profile.gender,
            dobStr,
            profile.firstName,
            profile.lastName,
            profile.email,
            phoneNum,
            profile.alt_email,
            profile.company
          );
          confirmUpdate(true);
          setSubmitting(false);
        } catch (e: any) {
          console.log('Error', e);
          e.data.forEach((err: any) => {
            setFieldError(err.meta.attribute, err.detail);
          });
          setSubmitting(false);
        }
      }
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ isSubmitting, dirty }) => {
          return (
            <Form noValidate>
              <AppBar className={classes.appBar} position="sticky">
                <Toolbar className={classes.toolbar}>
                  <div className={classes.box}>
                    <div className={classes.close}>
                      <IconButton onClick={handleClose}>
                        <CloseIcon className={classes.closeIcon} />
                      </IconButton>
                    </div>
                  </div>
                  <div className={classes.box}>
                    <Typography variant="h6" className={classes.title}>
                      Edit Mobile
                    </Typography>
                  </div>
                  <div className={classes.box}>
                    <div className={classes.actions}></div>
                  </div>
                </Toolbar>
              </AppBar>
              <Container maxWidth="md">
                <div className={classes.cardContainer}>
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <Field
                        component={Select}
                        name="countryCode"
                        label=""
                        fullWidth
                      >
                        {codes.map((code) => (
                          <MenuItem value={code.isoCode2} key={code.isoCode2}>
                            {'+' + code.countryCodes + ' ' + code.country}
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>
                    <Grid item xs={7}>
                      <Field
                        component={TextField}
                        className={classes.textField}
                        name="mobile"
                        autoFocus
                        fullWidth
                        variant="standard"
                        placeholder="Mobile Number"
                      />
                    </Grid>
                  </Grid>
                  <div className={classes.actionContainer}>
                    <Button
                      onClick={() => {
                        if (onClose) {
                          onClose({}, 'backdropClick');
                        }
                      }}
                      className={classes.buttonContainer}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      className={classes.buttonContainer}
                      disabled={isSubmitting || !dirty}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </Container>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
